import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, MessageCircle, PauseCircle, PlayCircle, Sparkles, Layers, MessageSquare } from 'lucide-react';
import { useStore } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { usePlayback } from '@/lib/playback';
import { cn } from '@/lib/utils';

type DisplayMode = 'comments' | 'funfacts' | 'both';

interface ReactionsTickerProps {
  roundId: string;
  submissionId: string;
  isActive: boolean;
  staticReactions?: {
    id: string;
    username: string;
    comment: string;
    points: number;
    created_at?: string;
  }[];
  variant?: 'list' | 'spotlight';
  funFacts?: string[];
  roundStatus?: 'submitting' | 'voting' | 'completed';
  submitterComment?: string; // Submitter's comment from the submission
  isArchive?: boolean; // Whether this is an archived playlist
}

export const ReactionsTicker = ({
  roundId,
  submissionId,
  isActive,
  staticReactions,
  variant = 'list',
  funFacts = [],
  roundStatus = 'completed',
  submitterComment,
  isArchive = false,
}: ReactionsTickerProps) => {
  // Debug logging
  console.log('[ReactionsTicker] 🔍 Props received:', {
    submissionId,
    roundId,
    isArchive,
    staticReactionsCount: staticReactions?.length || 0,
    staticReactions: staticReactions,
    funFactsCount: funFacts?.length || 0,
    funFacts: funFacts,
    submitterComment: submitterComment || 'none',
    roundStatus,
  });
  const { getVotesByRound, getSubmissionsByRound, state } = useStore();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [autoScroll, setAutoScroll] = useState(isActive);
  const [isHovered, setIsHovered] = useState(false);
  const { progressSeconds, durationSeconds } = usePlayback();
  const [manualIndex, setManualIndex] = useState(0);
  const manualIndexRef = useRef(0);
  const timeoutRef = useRef<number | null>(null);
  
  // Keep ref in sync with state
  useEffect(() => {
    manualIndexRef.current = manualIndex;
  }, [manualIndex]);
  
  // Determine if voting is closed (archived or completed)
  const isVotingClosed = isArchive || roundStatus === 'completed';
  
  // Determine default display mode based on round status
  const getDefaultDisplayMode = (): DisplayMode => {
    // For live playlists (voting not closed), default to 'both'
    if (!isVotingClosed) {
      return 'both';
    }
    // For archived playlists, default to 'both'
    return 'both';
  };
  
  const [displayMode, setDisplayMode] = useState<DisplayMode>(getDefaultDisplayMode());
  
  // Cycle through display modes
  const cycleDisplayMode = () => {
    setDisplayMode((prev) => {
      if (prev === 'comments') return 'funfacts';
      if (prev === 'funfacts') return 'both';
      return 'comments';
    });
  };
  
  // Update display mode when round status changes
  useEffect(() => {
    const newMode = getDefaultDisplayMode();
    console.log('[ReactionsTicker] 🔄 Setting display mode:', {
      currentMode: displayMode,
      newMode,
      isArchive,
      roundStatus,
      isVotingClosed,
    });
    setDisplayMode(newMode);
  }, [roundStatus, isArchive]);

  // Get submission to access submitter comment if not provided
  const submission = useMemo(() => {
    if (submitterComment !== undefined) {
      return null; // Already have the comment
    }
    const submissions = getSubmissionsByRound(roundId);
    return submissions.find(s => s.id === submissionId);
  }, [roundId, submissionId, submitterComment, getSubmissionsByRound]);

  const actualSubmitterComment = submitterComment ?? submission?.submitter_comment;

  const reactions = useMemo(() => {
    const allReactions: Array<{
      id: string;
      username: string;
      comment: string;
      points: number;
      created_at?: string;
      isSubmitter?: boolean;
    }> = [];

    // Add submitter comment if available
    if (actualSubmitterComment && actualSubmitterComment.trim()) {
      allReactions.push({
        id: `submitter-${submissionId}`,
        username: isVotingClosed ? (submission?.username || 'Submitter') : 'Anonymous',
        comment: actualSubmitterComment,
        points: 0, // Submitter comments don't have points
        created_at: submission?.submitted_at,
        isSubmitter: true,
      });
    }

    // Add voter comments (only for archived/completed rounds)
    if (isVotingClosed) {
      if (staticReactions && staticReactions.length > 0) {
        // Use static reactions (for archive data)
        // These are voter comments/reactions, not submitter comments
        staticReactions.forEach((reaction) => {
          allReactions.push({
            ...reaction,
            isSubmitter: false,
          });
        });
      } else {
        // Get voter comments from votes
        const allRoundVotes = getVotesByRound(roundId);
        console.log('[ReactionsTicker] Votes for round', roundId, ':', allRoundVotes.length);
        const votes = allRoundVotes.filter((vote) => vote.submission_id === submissionId);
        console.log('[ReactionsTicker] Votes for submission', submissionId, ':', votes.length);
        votes.forEach((vote) => {
          // Use username from vote if available (from join), otherwise try to find in state.users
          const username = vote.username || state.users.find((u) => u.id === vote.voter_id)?.username;
          console.log('[ReactionsTicker] Vote', vote.id, 'user_id:', vote.voter_id, 'username from vote:', vote.username, 'username from state:', state.users.find((u) => u.id === vote.voter_id)?.username, 'final username:', username);
          if (vote.comment && vote.comment.trim()) {
            allReactions.push({
              id: vote.id,
              username: username ?? 'Anonymous',
              comment: vote.comment,
              points: vote.points,
              created_at: vote.created_at,
              isSubmitter: false,
            });
          }
        });
      }
    }

    return allReactions.sort((a, b) => {
      const aTime = new Date(a.created_at ?? 0).getTime();
      const bTime = new Date(b.created_at ?? 0).getTime();
      return aTime - bTime;
    });
  }, [staticReactions, getVotesByRound, roundId, submissionId, state.users, actualSubmitterComment, isVotingClosed, submission]);

  const totalPoints = useMemo(
    () => reactions.reduce((sum, reaction) => sum + reaction.points, 0),
    [reactions],
  );

  // Determine what to show based on display mode and round status
  // For live playlists: show submitter comments (anonymously) when comments mode is selected
  // For archived playlists: show both submitter and voter comments when comments mode is selected
  const shouldShowComments = useMemo(
    () => (displayMode === 'comments' || displayMode === 'both') && reactions.length > 0,
    [displayMode, reactions.length],
  );
  const shouldShowFunFacts = useMemo(
    () => (displayMode === 'funfacts' || displayMode === 'both') && funFacts && funFacts.length > 0,
    [displayMode, funFacts],
  );

  // Calculate active index for reactions (only used when showing comments)
  const activeIndex = useMemo(() => {
    if (reactions.length === 0 || durationSeconds <= 0 || !shouldShowComments) {
      return -1;
    }

    const clamped = Math.max(0, Math.min(progressSeconds, durationSeconds));
    const ratio = clamped / durationSeconds;
    const index = Math.floor(ratio * reactions.length);
    return Math.min(index, reactions.length - 1);
  }, [reactions, progressSeconds, durationSeconds, shouldShowComments]);

  // Combine reactions and fun facts for display
  const displayItems = useMemo(() => {
    const items: Array<{ type: 'reaction' | 'funfact'; data: any; index: number }> = [];
    
    console.log('[ReactionsTicker] 🔍 Building displayItems:', {
      displayMode,
      shouldShowComments,
      shouldShowFunFacts,
      reactionsCount: reactions.length,
      funFactsCount: funFacts?.length || 0,
      funFacts: funFacts,
      reactions: reactions.slice(0, 3), // First 3 reactions for debugging
    });
    
    if (shouldShowComments && reactions.length > 0) {
      reactions.forEach((reaction, idx) => {
        items.push({ type: 'reaction', data: reaction, index: idx });
      });
      console.log('[ReactionsTicker] ✅ Added', reactions.length, 'reactions to displayItems');
    } else {
      console.log('[ReactionsTicker] ⚠️ Not adding reactions:', {
        shouldShowComments,
        reactionsLength: reactions.length,
      });
    }
    
    if (shouldShowFunFacts && funFacts) {
      funFacts.forEach((fact, idx) => {
        items.push({ type: 'funfact', data: { text: fact, id: `funfact-${idx}` }, index: idx });
      });
      console.log('[ReactionsTicker] ✅ Added', funFacts.length, 'fun facts to displayItems');
    } else {
      console.log('[ReactionsTicker] ⚠️ Not adding fun facts:', {
        shouldShowFunFacts,
        funFactsExists: !!funFacts,
        funFactsLength: funFacts?.length || 0,
        funFactsValue: funFacts,
      });
    }
    
    console.log('[ReactionsTicker] 📊 Final displayItems count:', items.length);
    return items;
  }, [shouldShowComments, shouldShowFunFacts, reactions, funFacts, displayMode]);

  useEffect(() => {
    setAutoScroll(isActive);
  }, [isActive]);

  useEffect(() => {
    if (variant !== 'spotlight') {
      return;
    }
    setManualIndex(0);
  }, [submissionId, variant]);

  // Calculate display time based on content type and length
  const getDisplayTime = useCallback((item: { type: 'reaction' | 'funfact'; data: any } | null) => {
    if (!item) return 8000; // Default 8 seconds
    
    if (item.type === 'funfact') {
      // For fun facts: 8-12 seconds based on text length
      const textLength = item.data.text?.length || 0;
      // Base time: 8 seconds, add up to 4 seconds based on length (max ~500 chars)
      const additionalTime = Math.min((textLength / 500) * 4000, 4000);
      return 8000 + additionalTime;
    } else {
      // For reactions: 5 seconds if no comment, 10 seconds if there's a comment
      return item.data.comment ? 10000 : 5000;
    }
  }, []);

  // Calculate active index based on displayItems (for spotlight variant)
  const displayActiveIndex = useMemo(() => {
    if (displayItems.length === 0 || durationSeconds <= 0) {
      return -1;
    }
    const clamped = Math.max(0, Math.min(progressSeconds, durationSeconds));
    const ratio = clamped / durationSeconds;
    const index = Math.floor(ratio * displayItems.length);
    return Math.min(index, displayItems.length - 1);
  }, [displayItems, progressSeconds, durationSeconds]);

  useEffect(() => {
    if (variant !== 'spotlight') {
      return;
    }

    console.log('[ReactionsTicker] 🔄 Auto-scroll effect:', {
      durationSeconds,
      autoScroll,
      displayActiveIndex,
      displayItemsLength: displayItems.length,
      manualIndex: manualIndexRef.current,
    });

    // Sync with playback progress for comments (only if we have reactions and duration)
    const hasReactions = reactions.length > 0;
    const shouldSyncWithPlayback = durationSeconds > 0 && autoScroll && displayActiveIndex >= 0 && hasReactions;
    
    if (shouldSyncWithPlayback) {
      setManualIndex(displayActiveIndex);
      return;
    }

    // For fun facts or when not syncing with playback, use manual auto-scroll
    if (!autoScroll || displayItems.length <= 1) {
      console.log('[ReactionsTicker] ⚠️ Auto-scroll disabled or not enough items:', {
        autoScroll,
        displayItemsLength: displayItems.length,
      });
      return;
    }

    // Use a recursive timeout instead of interval to recalculate display time for each item
    const scheduleNext = () => {
      const currentIndex = manualIndexRef.current % displayItems.length;
      const currentItem = displayItems[currentIndex];
      const displayTime = getDisplayTime(currentItem);
      
      console.log('[ReactionsTicker] ⏭️ Scheduling next item:', {
        currentIndex,
        totalItems: displayItems.length,
        displayTime,
        itemType: currentItem?.type,
      });

      timeoutRef.current = window.setTimeout(() => {
        const nextIndex = (manualIndexRef.current + 1) % displayItems.length;
        console.log('[ReactionsTicker] ➡️ Moving to next item:', {
          from: manualIndexRef.current,
          to: nextIndex,
          totalItems: displayItems.length,
        });
        setManualIndex(nextIndex);
        scheduleNext();
      }, displayTime);
    };

    scheduleNext();

    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [variant, autoScroll, displayItems.length, durationSeconds, displayActiveIndex, getDisplayTime, reactions.length]);

  useEffect(() => {
    if (variant !== 'list') {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }
    container.scrollTo({ top: 0 });
  }, [submissionId, variant]);

  useEffect(() => {
    if (variant !== 'list') {
      return;
    }
    if (!autoScroll || isHovered) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    const interval = window.setInterval(() => {
      const maxScroll = container.scrollHeight - container.clientHeight;
      if (maxScroll <= 0) {
        return;
      }

      const nextScrollTop =
        container.scrollTop + container.clientHeight >= maxScroll
          ? 0
          : container.scrollTop + container.clientHeight;

      container.scrollTo({ top: nextScrollTop, behavior: 'smooth' });
    }, 4000);

    return () => {
      window.clearInterval(interval);
    };
  }, [autoScroll, isHovered, reactions.length, variant]);

  useEffect(() => {
    if (variant !== 'list') {
      return;
    }
    if (!autoScroll || isHovered) {
      return;
    }
    if (activeIndex < 0) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    // Find the active reaction item in displayItems
    const activeItemIndex = displayItems.findIndex((item, idx) => 
      item.type === 'reaction' && shouldShowComments && idx === activeIndex
    );
    
    if (activeItemIndex < 0) {
      return;
    }
    
    const element = container.querySelector<HTMLElement>(`[data-reaction-index="${activeItemIndex}"]`);
    if (!element) {
      return;
    }

    const offset = element.offsetTop - container.clientHeight / 2 + element.clientHeight / 2;
    container.scrollTo({
      top: Math.max(0, offset),
      behavior: 'smooth',
    });
  }, [activeIndex, autoScroll, isHovered, variant, displayItems, shouldShowComments]);

  const formatRelativeTime = (timestamp?: string) => {
    if (!timestamp) {
      return "";
    }
    const now = Date.now();
    const created = new Date(timestamp).getTime();
    const diffMs = now - created;
    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (minutes < 1) return 'moments ago';
    if (minutes < 60) return `${minutes} min ago`;
    if (hours < 24) return `${hours} hr${hours > 1 ? 's' : ''} ago`;
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };
  
  // Get display mode icon
  const getDisplayModeIcon = () => {
    switch (displayMode) {
      case 'comments':
        return <MessageSquare className="h-4 w-4" />;
      case 'funfacts':
        return <Sparkles className="h-4 w-4" />;
      case 'both':
        return <Layers className="h-4 w-4" />;
    }
  };
  
  // Always show the component with toggle, even if there's no content yet
  // This allows users to toggle between modes even when content is loading

  if (variant === 'spotlight') {
    const syncWithPlayback = durationSeconds > 0 && autoScroll && displayActiveIndex >= 0;
    const effectiveIndex = syncWithPlayback
      ? displayActiveIndex
      : displayItems.length > 0 ? manualIndex % displayItems.length : 0;
    const item = displayItems.length > 0 ? (displayItems[effectiveIndex] ?? displayItems[0]) : null;

    const handlePrev = () => {
      if (displayItems.length > 0) {
        setManualIndex((prev) => (prev - 1 + displayItems.length) % displayItems.length);
      }
    };

    const handleNext = () => {
      if (displayItems.length > 0) {
        setManualIndex((prev) => (prev + 1) % displayItems.length);
      }
    };

    return (
      <div className="flex h-full flex-col gap-3 rounded-xl border border-muted/70 bg-muted/20 p-4">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'h-2 w-2 rounded-full',
                autoScroll ? 'bg-green-500 animate-pulse' : 'bg-muted-foreground',
              )}
            />
            <span>
              {displayMode === 'comments' ? 'Scores & Comments' :
               displayMode === 'funfacts' ? 'Fun Facts' :
               'Scores, Comments & Fun Facts'}
            </span>
            {shouldShowComments && (
              <>
                <span className="text-muted-foreground/70">· {reactions.length} reactions</span>
                <span className="text-muted-foreground/70">· {Math.round(totalPoints)} pts</span>
              </>
            )}
            {shouldShowFunFacts && funFacts && (
              <span className="text-muted-foreground/70">· {funFacts.length} facts</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={cycleDisplayMode}
              className="h-7 w-7"
              title={`Switch to ${displayMode === 'comments' ? 'fun facts' : displayMode === 'funfacts' ? 'both' : 'comments only'}`}
            >
              {getDisplayModeIcon()}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAutoScroll((prev) => !prev)}
              className="h-7 px-2 text-xs"
            >
              {autoScroll ? (
                <>
                  <PauseCircle className="mr-1 h-3.5 w-3.5" />
                  Pause
                </>
              ) : (
                <>
                  <PlayCircle className="mr-1 h-3.5 w-3.5" />
                  Auto
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="relative flex flex-1 items-center justify-center overflow-hidden rounded-2xl border border-muted/50 bg-background/95 px-6 py-8 shadow-xl">
          {item ? (
          <div
            key={item?.data?.id ?? 'empty'}
            className="flex w-full flex-col gap-5 text-base text-muted-foreground transition-opacity duration-500 sm:text-lg"
          >
            {item?.type === 'funfact' ? (
              // Fun fact display
              <>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Fun Fact</span>
                </div>
                <p className="text-balance text-2xl font-semibold leading-snug text-foreground sm:text-3xl">
                  {item.data.text}
                </p>
              </>
            ) : item?.data?.comment ? (
              // Has comment: username on left, score on right (if not submitter)
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {item.data.isSubmitter && (
                      <span className="rounded-md bg-purple-500/20 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-purple-600 dark:text-purple-400">
                        Submitter
                      </span>
                    )}
                    <span className="text-lg font-semibold text-foreground sm:text-xl">
                      {item.data.isSubmitter ? 'Submitter' : item.data.username}
                    </span>
                  </div>
                  {!item.data.isSubmitter && item.data.points !== undefined && (
                    <span className="rounded-full bg-primary/10 px-3 py-1 text-lg font-semibold text-primary sm:text-xl">
                      {item.data.points >= 0 ? `+${item.data.points}` : item.data.points}
                    </span>
                  )}
                </div>
                <p className="text-balance text-2xl font-semibold leading-snug text-foreground sm:text-3xl">
                  {item.data.comment}
                </p>
              </>
            ) : (
              // No comment: username and score as bigger number taking the space (only for voters)
              !item.data.isSubmitter && (
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-semibold text-foreground sm:text-3xl">{item.data.username}</span>
                  {item.data.points !== undefined && (
                    <span className="text-4xl font-bold text-primary sm:text-5xl">
                      {item.data.points >= 0 ? `+${item.data.points}` : item.data.points}
                    </span>
                  )}
                </div>
              )
            )}
          </div>
          ) : (
            <div className="text-center text-muted-foreground">
              <p>No content to display</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {displayItems.length > 0 ? `${effectiveIndex + 1} / ${displayItems.length}` : '0 / 0'}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={handlePrev}
              disabled={displayItems.length <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={handleNext}
              disabled={displayItems.length <= 1}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col rounded-lg border border-muted bg-muted/20">
      <div className="flex items-center justify-between px-4 pt-3 text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <span
            className={`h-2 w-2 rounded-full ${
              autoScroll && !isHovered ? 'bg-green-500 animate-pulse' : 'bg-muted-foreground'
            }`}
          />
          <span>
            {displayMode === 'comments' ? 'Live reactions' :
             displayMode === 'funfacts' ? 'Fun Facts' :
             'Live reactions & Fun Facts'}
          </span>
          {shouldShowComments && (
            <>
              <span className="text-muted-foreground/70">· {reactions.length} entries</span>
              <span className="text-muted-foreground/70">· {Math.round(totalPoints)} total pts</span>
            </>
          )}
          {shouldShowFunFacts && funFacts && (
            <span className="text-muted-foreground/70">· {funFacts.length} facts</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={cycleDisplayMode}
            className="h-7 w-7"
            title={`Switch to ${displayMode === 'comments' ? 'fun facts' : displayMode === 'funfacts' ? 'both' : 'comments only'}`}
          >
            {getDisplayModeIcon()}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAutoScroll((prev) => !prev)}
            className="h-7 px-2 text-xs"
          >
            {autoScroll ? (
              <>
                <PauseCircle className="mr-1 h-3.5 w-3.5" />
                Pause
              </>
            ) : (
              <>
                <PlayCircle className="mr-1 h-3.5 w-3.5" />
                Auto play
              </>
            )}
          </Button>
        </div>
      </div>
      <div
        ref={containerRef}
        className="mt-3 h-[280px] w-full flex-1 overflow-y-auto rounded-b-lg border-t border-muted/60 bg-muted/30 p-4"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="space-y-4 pr-2">
          {displayItems.map((item, index) => {
            // Calculate if this item is active (only for reactions in completed rounds)
            const isActive = item.type === 'reaction' && shouldShowComments && index === activeIndex;
            const isPast = item.type === 'reaction' && shouldShowComments && index < activeIndex;
            
            return (
            <div
              key={item.data.id}
              data-reaction-index={index}
              className={cn(
                "rounded-md border p-3 shadow-sm transition-transform hover:-translate-y-0.5",
                isActive
                  ? "border-primary/40 bg-primary/10"
                  : isPast
                  ? "border-muted-foreground/20 bg-muted/20"
                  : item.type === 'funfact'
                  ? "border-primary/20 bg-primary/5"
                  : "border-transparent bg-background",
              )}
            >
              {item.type === 'funfact' ? (
                // Fun fact display
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <span className="text-xs font-semibold text-primary uppercase tracking-wide">Fun Fact</span>
                  </div>
                  <p className="text-sm text-foreground break-words">{item.data.text}</p>
                </>
              ) : item.data.comment ? (
                // Has comment: username on left, score on right (if not submitter)
                <>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center gap-2">
                      {item.data.isSubmitter && (
                        <span className="rounded-md bg-purple-500/20 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-purple-600 dark:text-purple-400">
                          Submitter
                        </span>
                      )}
                      <span className="font-medium text-foreground">
                        {item.data.isSubmitter ? 'Submitter' : item.data.username}
                      </span>
                    </div>
                    <span className="flex items-center gap-2">
                      {!item.data.isSubmitter && item.data.points !== undefined && (
                        <span className="rounded bg-primary/10 px-2 py-0.5 font-semibold text-primary">
                          {item.data.points >= 0 ? `+${item.data.points}` : item.data.points}
                        </span>
                      )}
                      {formatRelativeTime(item.data.created_at) && (
                        <span>{formatRelativeTime(item.data.created_at)}</span>
                      )}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground break-words">{item.data.comment}</p>
                </>
              ) : (
                // No comment: username and score as bigger number taking the space (only for voters)
                !item.data.isSubmitter && (
                  <div className="flex items-center justify-between">
                    <span className="text-base font-semibold text-foreground">{item.data.username}</span>
                    {item.data.points !== undefined && (
                      <span className="text-2xl font-bold text-primary">
                        {item.data.points >= 0 ? `+${item.data.points}` : item.data.points}
                      </span>
                    )}
                  </div>
                )
              )}
            </div>
          );
          })}
        </div>
      </div>
    </div>
  );
};


